<template>
  <div class="px-4 py-4 sm:px-6">
    <div class="flex flex-wrap items-center gap-4">
      <slot name="checkboxes">
        <div class="flex flex-wrap items-center gap-3">
          <label
            v-for="checkbox in checkboxes"
            :key="checkbox.key"
            :for="`filter-checkbox-${checkbox.key}`"
            class="inline-flex items-center gap-2"
          >
            <input
              :id="`filter-checkbox-${checkbox.key}`"
              :checked="checkbox.value"
              class="w-4 h-4 bg-white border-gray-300 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1 accent-primary transition"
              type="checkbox"
              @change="onCheckboxChange(checkbox.key, $event.target.checked)"
            />
            <span class="text-sm text-gray-700">{{ checkbox.label }}</span>
          </label>
        </div>
      </slot>

      <slot v-if="showDateRange" name="date-range">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <span class="text-sm font-medium text-gray-700">Date Range:</span>
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-2">
            <div class="relative">
              <button
                class="flex min-w-[180px] items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1"
                type="button"
                @click="toggleStartCalendar"
              >
                <span>{{ startLabel }}</span>
                <svg
                  class="h-4 w-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M19 9l-7 7-7-7"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                  />
                </svg>
              </button>
              <div v-if="isStartCalendarVisible" class="absolute z-20 mt-2">
                <DateTimePicker
                  :hide-button="true"
                  :model-value="startModelValue"
                  :select-past-time="true"
                  :show-time="false"
                  :title="'Start Date'"
                  @applyDateTime="handleStartApply"
                  @isCalendarOpen="handleStartCalendarState"
                />
              </div>
            </div>

            <span class="text-sm text-gray-500">to</span>

            <div class="relative">
              <button
                class="flex min-w-[180px] items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1"
                type="button"
                @click="toggleEndCalendar"
              >
                <span>{{ endLabel }}</span>
                <svg
                  class="h-4 w-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M19 9l-7 7-7-7"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                  />
                </svg>
              </button>
              <div v-if="isEndCalendarVisible" class="absolute z-20 mt-2">
                <DateTimePicker
                  :hide-button="true"
                  :model-value="endModelValue"
                  :select-past-time="true"
                  :show-time="false"
                  :title="'End Date'"
                  @applyDateTime="handleEndApply"
                  @isCalendarOpen="handleEndCalendarState"
                />
              </div>
            </div>
          </div>
        </div>
      </slot>

      <slot name="actions">
        <button
          class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1 sm:w-auto"
          type="button"
          @click="handleClearFilters"
        >
          Clear Filters
        </button>
      </slot>
    </div>
  </div>
</template>

<script>
import { DateTime } from "luxon";
import DateTimePicker from "@/components/DateTimePicker.vue";

export default {
  name: "TableFilters",
  components: {
    DateTimePicker,
  },
  props: {
    checkboxes: { type: Array, default: () => [] },
    dateRange: { type: Object, default: () => ({ start: "", end: "" }) },
    showDateRange: { type: Boolean, default: false },
  },
  emits: ["checkbox-change", "date-change", "clear-filters"],
  data() {
    return {
      isStartCalendarVisible: false,
      isEndCalendarVisible: false,
    };
  },
  computed: {
    startLabel() {
      return this.formatDateLabel(this.dateRange?.start, "Start Date");
    },
    endLabel() {
      return this.formatDateLabel(this.dateRange?.end, "End Date");
    },
    startModelValue() {
      return this.toModelValue(this.dateRange?.start);
    },
    endModelValue() {
      return this.toModelValue(this.dateRange?.end);
    },
  },
  methods: {
    onCheckboxChange(key, value) {
      this.$emit("checkbox-change", { key, value });
    },
    toggleStartCalendar() {
      this.isStartCalendarVisible = !this.isStartCalendarVisible;
      if (this.isStartCalendarVisible) {
        this.isEndCalendarVisible = false;
      }
    },
    toggleEndCalendar() {
      this.isEndCalendarVisible = !this.isEndCalendarVisible;
      if (this.isEndCalendarVisible) {
        this.isStartCalendarVisible = false;
      }
    },
    handleStartApply({ selectedDateTime }) {
      this.emitDateChange("start", this.toIsoDate(selectedDateTime));
      this.isStartCalendarVisible = false;
    },
    handleEndApply({ selectedDateTime }) {
      this.emitDateChange("end", this.toIsoDate(selectedDateTime));
      this.isEndCalendarVisible = false;
    },
    handleStartCalendarState(isOpen) {
      if (!isOpen) {
        this.isStartCalendarVisible = false;
      }
    },
    handleEndCalendarState(isOpen) {
      if (!isOpen) {
        this.isEndCalendarVisible = false;
      }
    },
    emitDateChange(type, value) {
      this.$emit("date-change", { type, value });
    },
    handleClearFilters() {
      this.isStartCalendarVisible = false;
      this.isEndCalendarVisible = false;
      this.$emit("clear-filters");
    },
    formatDateLabel(value, fallback) {
      if (!value) {
        return fallback;
      }
      const parsed = DateTime.fromISO(value);
      return parsed.isValid ? parsed.toFormat("dd MMM yyyy") : fallback;
    },
    toModelValue(value) {
      if (!value) {
        return null;
      }
      const parsed = DateTime.fromISO(value);
      return parsed.isValid ? parsed.toISO() : null;
    },
    toIsoDate(value) {
      if (!value) {
        return "";
      }
      const parsed = DateTime.fromISO(value);
      return parsed.isValid ? parsed.toISODate() : "";
    },
  },
};
</script>
